import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useConfig } from '@/contexts/ConfigContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Favorite, Share, ContentCopy, Check } from '@mui/icons-material';

const ExportContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
    align-items: flex-start;
    width: 100%;
    margin: 0 auto;
`;

const Header = styled.h2`
    color: #fff;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 24px;
    font-weight: 700;
    margin-bottom: 5px;
    text-transform: uppercase;
    margin-left: 6px;
`;

const SubHeader = styled.p`
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    margin: 0 0 20px 0;
`;

const SectionTitle = styled.h3`
    color: #fff;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const SupportSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 24px;
    width: 100%;
    margin-bottom: 10px;
`;

const Text = styled.p`
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
`;

const ButtonGroup = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 5px;
`;

const SupportButton = styled.a`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 24px;
    border-radius: 8px;
    font-weight: 700;
    color: white;
    text-decoration: none;
    transition: all 0.2s;
    font-family: 'Chakra Petch', sans-serif;
    text-transform: uppercase;
    font-size: 13px;
    border: none;
    cursor: pointer;

    &:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
        box-shadow: 0 5px 15px rgba(0,0,0,0.3);
    }
`;

const PatreonButton = styled(SupportButton)`
    background: #ff424d; 
    box-shadow: 0 4px 12px rgba(255, 66, 77, 0.2);
`;

const SubStarButton = styled(SupportButton)`
    background: #e1306c;
    box-shadow: 0 4px 12px rgba(225, 48, 108, 0.2);
`;

const PriceTag = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #4ade80;
    font-weight: 800;
    font-family: 'Chakra Petch', sans-serif;
    font-size: 14px;
    letter-spacing: 0.5px;
    position: relative;
    overflow: hidden;

    &::before {
        content: '$';
        font-size: 10px;
        margin-right: 2px;
        color: rgba(74, 222, 128, 0.6);
    }
`;

const CodeArea = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 10px;
`;

const Label = styled.label`
    font-size: 11px;
    color: rgba(255, 179, 0, 1);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 1px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-left: 4px;
`;

const TextArea = styled.textarea`
    width: 100%;
    height: 280px;
    background: #0d1117;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 16px;
    color: ${props => props.$success ? '#4ade80' : '#a78bfa'};
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 12px;
    line-height: 1.5;
    resize: none;
    outline: none;
    transition: border-color 0.2s, color 1s ease;

    &:focus {
        border-color: #6366f1;
    }
    
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255,255,255,0.1);
        border-radius: 4px;
    }
`;

const Input = styled.input`
    width: 100%;
    background: #0d1117;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 12px 16px;
    color: ${props => props.$success ? '#4ade80' : '#a78bfa'};
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 12px;
    outline: none;
    transition: border-color 0.2s, color 1s ease;

    &:focus {
        border-color: #6366f1;
    }
`;

const CopyButton = styled.button`
    background: transparent;
    border: none;
    color: ${props => props.$success ? '#4ade80' : '#6366f1'};
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-left: auto;
    transition: color 1s ease;
    margin-right: 10px;

    &:hover {
        color: ${props => props.$success ? '#4ade80' : '#818cf8'};
    }
`;

const ExportPage = () => {
    const { t } = useLanguage();
    const { generateConfigString, configData } = useConfig();
    const [cfgContent, setCfgContent] = useState('');
    const [consoleContent, setConsoleContent] = useState('');
    const [copyStatus, setCopyStatus] = useState({}); // { [key]: boolean }

    useEffect(() => {
        // Generate CFG content (Display format)
        // Generate CFG content (Display format)
        const exportData = {
            ...configData,
            _meta: {
                activeModules: ['crosshair', 'mouse']
            }
        };
        const displayString = generateConfigString({ format: 'display' }, exportData);
        setCfgContent(displayString);

        // Generate Console content (One line separated by semicolons)
        const lines = displayString.split('\n');
        const cleanLines = lines
            .map(line => line.trim())
            .filter(line => line && !line.startsWith('//') && !line.startsWith('#'));

        setConsoleContent(cleanLines.join('; '));
    }, [generateConfigString, configData]);

    const copyToClipboard = (text, key) => {
        navigator.clipboard.writeText(text);

        // Set success status for specific key
        setCopyStatus(prev => ({ ...prev, [key]: true }));

        // Reset after 3 seconds
        setTimeout(() => {
            setCopyStatus(prev => ({ ...prev, [key]: false }));
        }, 1000);
    };

    const handleCopyModule = (moduleName) => {
        // We need to trick generateConfigString by passing a custom data object with override meta
        // But ConfigContext's generateConfigString reads activeModules from dataOverride if provided

        const dummyData = {
            ...configData,
            _meta: {
                activeModules: [moduleName]
            }
        };

        const moduleString = generateConfigString({ format: 'display' }, dummyData);
        copyToClipboard(moduleString, moduleName);
    };

    return (

        <ExportContainer>
            <Header>{t('EXPORT_TITLE')}</Header>

            <SupportSection>
                <SectionTitle><Favorite style={{ fontSize: 18, color: '#ec4899' }} /> {t('SUPPORT_TITLE')}</SectionTitle>
                <Text>
                    {t('SUPPORT_TEXT')}
                </Text>

                <ButtonGroup>
                    <PriceTag>3.99</PriceTag>
                    <PatreonButton href="https://www.patreon.com/ConfigStrike" target="_blank">
                        {t('JOIN_PATREON')}
                    </PatreonButton>
                    <SubStarButton href="https://www.subscribestar.com/configstrike" target="_blank">
                        {t('SUPPORT_SUBSCRIBESTAR')}
                    </SubStarButton>
                </ButtonGroup>
            </SupportSection>

            <SupportSection>
                <SectionTitle><Share style={{ fontSize: 18, color: '#6366f1' }} /> {t('CANT_SUBSCRIBE_TITLE')}</SectionTitle>
                <Text>
                    {t('CANT_SUBSCRIBE_TEXT')}
                </Text>
            </SupportSection>

            <CodeArea>
                <Label>
                    {t('CFG_LABEL')}
                    <CopyButton
                        onClick={() => copyToClipboard(cfgContent, 'all_cfg')}
                        $success={copyStatus['all_cfg']}
                    >
                        {copyStatus['all_cfg'] ? <Check style={{ fontSize: 14 }} /> : <ContentCopy style={{ fontSize: 14 }} />}
                        {copyStatus['all_cfg'] ? t('COPIED') : t('COPY_ALL')}
                    </CopyButton>
                </Label>
                <TextArea
                    readOnly
                    value={cfgContent}
                    onClick={(e) => e.target.select()}
                    $success={copyStatus['all_cfg']}
                />
            </CodeArea>

            <CodeArea>
                <Label>
                    {t('CONSOLE_LABEL')}
                    <CopyButton
                        onClick={() => copyToClipboard(consoleContent, 'all_console')}
                        $success={copyStatus['all_console']}
                    >
                        {copyStatus['all_console'] ? <Check style={{ fontSize: 14 }} /> : <ContentCopy style={{ fontSize: 14 }} />}
                        {copyStatus['all_console'] ? t('COPIED') : t('COPY_ALL')}
                    </CopyButton>
                </Label>
                <Input
                    readOnly
                    value={consoleContent}
                    onClick={(e) => e.target.select()}
                    $success={copyStatus['all_console']}
                />
            </CodeArea>

        </ExportContainer>
    );
};

export default ExportPage;
